import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Thermometer, Droplets, Wind, Sprout, X, Sun, Moon, CheckCircle, AlertCircle, Clock, Calendar, Layers, Image as ImageIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format, differenceInDays } from 'date-fns';
import TaskPhotoThumb from '@/components/TaskPhotoThumb';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Safely format a date value; returns the fallback for missing/invalid dates
// instead of throwing a RangeError ("Invalid time value").
const safeFormat = (value, fmt, fallback = '-') => {
  if (value === null || value === undefined || value === '') return fallback;
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return fallback;
  try {
    return format(d, fmt);
  } catch (e) {
    return fallback;
  }
};

// Room Detail Popup Component
const RoomDetailPopup = ({ room, cycle, onClose }) => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [temperatureData, setTemperatureData] = useState({ morning: null, evening: null });
  const [wateringData, setWateringData] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  // Calculate actual current day based on start date
  const calculateCurrentDay = () => {
    if (!cycle?.start_date) return cycle?.current_day || 0;
    const startDate = new Date(cycle.start_date);
    const today = new Date();
    return differenceInDays(today, startDate) + 1;
  };

  const actualCurrentDay = calculateCurrentDay();

  // Fetch data for the selected date
  const fetchRoomDetails = async (dateStr) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch temperature logs for this room
      const tempRes = await axios.get(`${API}/temperature-logs?room_id=${room.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // /api/temperature-logs returns { logs, total } — unwrap
      const tempLogs = Array.isArray(tempRes.data)
        ? tempRes.data
        : (tempRes.data?.logs || []);

      // Filter logs for the selected date
      const dateLogs = tempLogs.filter(log =>
        log.log_date && log.log_date.startsWith(dateStr)
      );
      
      // Find morning and evening logs by shift field or time
      const morningLog = dateLogs.find(log => log.shift === 'morning') || 
        dateLogs.find(log => new Date(log.log_date).getHours() < 12);
      const eveningLog = dateLogs.find(log => log.shift === 'evening') ||
        dateLogs.find(log => new Date(log.log_date).getHours() >= 12);
      
      setTemperatureData({ morning: morningLog, evening: eveningLog });
      
      // Fetch watering schedule/logs for this room
      const waterRes = await axios.get(`${API}/watering/logs?room_id=${room.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: { logs: [] } }));
      
      // Filter for the selected date - handle both array and object response
      const logsData = waterRes.data?.logs || waterRes.data || [];
      const dateWatering = Array.isArray(logsData) ? logsData.filter(w => 
        w.log_date && w.log_date.startsWith(dateStr)
      ) : [];
      setWateringData(dateWatering);
      
    } catch (error) {
      console.error('Failed to fetch room details:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoomDetails(selectedDate);
  }, [room.id, selectedDate]);

  // Fetch task-completion photos (growing + compost) for this room's current cycle
  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/room-cycles/room-photos/${room.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPhotos(res.data?.photos || []);
      } catch (e) {
        setPhotos([]);
      }
    };
    fetchPhotos();
  }, [room.id]);

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const isToday = selectedDate === new Date().toISOString().split('T')[0];

  const renderTemperatureContent = (log, shift) => {
    if (!log) {
      return (
        <div className="text-center py-8 text-gray-500">
          <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>No {shift} temperature recorded {isToday ? 'today' : `on ${safeFormat(selectedDate, 'MMM d, yyyy')}`}</p>
        </div>
      );
    }

    // Get compost readings from either compost_readings or compost_temperatures field
    const compostReadings = log.compost_readings || log.compost_temperatures || [];
    // Pairing is determined by the ROOM (source of truth), not the log payload.
    // Only rooms whose name contains "&" (e.g. "ROOM 7&8") are paired.
    const roomName = room?.name || log.room_name || '';
    const isPaired = roomName.includes('&');
    const pairLabels = (() => {
      const m = roomName.match(/(\d+)\s*&\s*(\d+)/);
      return m ? [`Room ${m[1]}`, `Room ${m[2]}`] : ['Room 1', 'Room 2'];
    })();

    return (
      <div className="space-y-4 max-h-[400px] overflow-y-auto">
        {/* Air Temperature(s) */}
        {isPaired ? (
          <div className="p-3 bg-blue-50 rounded-lg space-y-2">
            <p className="text-sm font-medium">Air Temperature</p>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col items-center p-2 bg-white rounded">
                <span className="text-xs text-gray-500">{pairLabels[0]}</span>
                <span className="text-lg font-bold text-blue-600">
                  {log.air_temperature_1 != null ? `${log.air_temperature_1}°C` : '—'}
                </span>
              </div>
              <div className="flex flex-col items-center p-2 bg-white rounded">
                <span className="text-xs text-gray-500">{pairLabels[1]}</span>
                <span className="text-lg font-bold text-blue-600">
                  {log.air_temperature_2 != null ? `${log.air_temperature_2}°C` : '—'}
                </span>
              </div>
            </div>
            <p className="text-xs text-blue-700 text-right">
              Avg: <span className="font-semibold">{log.air_temperature}°C</span>
            </p>
          </div>
        ) : (
          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
            <span className="text-sm font-medium">Air Temperature</span>
            <span className="text-lg font-bold text-blue-600">{log.air_temperature != null ? `${log.air_temperature}°C` : '—'}</span>
          </div>
        )}

        {/* CO2 Level(s) */}
        {isPaired ? (
          <div className="p-3 bg-gray-50 rounded-lg space-y-2">
            <p className="text-sm font-medium">CO2 Level</p>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col items-center p-2 bg-white rounded">
                <span className="text-xs text-gray-500">{pairLabels[0]}</span>
                <span className="text-lg font-bold text-gray-700">
                  {log.co2_level_1 != null ? `${log.co2_level_1} ppm` : '—'}
                </span>
              </div>
              <div className="flex flex-col items-center p-2 bg-white rounded">
                <span className="text-xs text-gray-500">{pairLabels[1]}</span>
                <span className="text-lg font-bold text-gray-700">
                  {log.co2_level_2 != null ? `${log.co2_level_2} ppm` : '—'}
                </span>
              </div>
            </div>
            {log.co2_level != null && (
              <p className="text-xs text-gray-600 text-right">
                Avg: <span className="font-semibold">{log.co2_level} ppm</span>
              </p>
            )}
          </div>
        ) : (
          log.co2_level && (
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm font-medium">CO2 Level</span>
              <span className="text-lg font-bold text-gray-600">{log.co2_level} ppm</span>
            </div>
          )
        )}
        
        {/* Compost Temperature Readings (partial allowed) */}
        {compostReadings.length > 0 && (
          <div className="p-3 bg-orange-50 rounded-lg">
            <p className="text-sm font-medium mb-2">Compost Temperature Readings — {compostReadings.length}/18 (°C)</p>
            <div className="grid grid-cols-6 gap-2">
              {compostReadings.map((temp, idx) => {
                const val = parseFloat(temp.temperature || temp.value);
                const isOutlier = isNaN(val) || val <= 0 || val > 50;
                return (
                  <div key={idx} className={`text-center p-2 bg-white rounded border ${isOutlier ? 'border-red-300' : ''}`}>
                    <span className="text-xs text-gray-500">#{idx + 1}</span>
                    <p className={`font-medium ${isOutlier ? 'text-red-600' : 'text-orange-600'}`}>
                      {temp.temperature || temp.value}°C
                    </p>
                  </div>
                );
              })}
            </div>
            {/* Average — outliers (>50°C or ≤0°C) excluded */}
            {(() => {
              const validReadings = compostReadings
                .map(t => parseFloat(t.temperature || t.value))
                .filter(v => !isNaN(v) && v > 0 && v <= 50);
              const outlierCount = compostReadings.length - validReadings.length;
              const avg = validReadings.length > 0
                ? (validReadings.reduce((sum, v) => sum + v, 0) / validReadings.length).toFixed(1)
                : null;
              return (
                <div className="mt-2 pt-2 border-t border-orange-200">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Average Compost Temp</span>
                    <span className="font-bold text-orange-700">{avg !== null ? `${avg}°C` : '—'}</span>
                  </div>
                  {outlierCount > 0 && (
                    <p className="text-xs text-red-600 mt-1">
                      {outlierCount} reading{outlierCount > 1 ? 's' : ''} excluded as outlier (likely typo — value out of range 0–50°C)
                    </p>
                  )}
                </div>
              );
            })()}
          </div>
        )}
        
        <p className="text-xs text-gray-500 text-right">
          Logged at: {safeFormat(log.log_date, 'h:mm a')}
        </p>
      </div>
    );
  };

  // Render key dates for the cycle
  const renderDatesContent = () => {
    if (!cycle) {
      return (
        <div className="text-center py-8 text-gray-500">
          <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>No active cycle</p>
        </div>
      );
    }

    const dateFields = [
      { label: 'Start Date (Day 1)', field: 'start_date', day: 1 },
      { label: 'Casing Date (Day 13)', field: 'casing_date', day: 13 },
      { label: 'Ruffling Date (Day 19)', field: 'ruffling_date', day: 19 },
      { label: 'Covering Date (Day 21)', field: 'covering_date', day: 21 },
      { label: 'Airing Start (Day 21)', field: 'airing_start_date', day: 21 },
      { label: 'Flush 1 Start (Day 33)', field: 'flush1_start_date', day: 33 },
      { label: 'Flush 2 Start (Day 41)', field: 'flush2_start_date', day: 41 },
      { label: 'Expected End (Day 45)', field: 'expected_completion', day: 45 },
    ];

    return (
      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {dateFields.map(({ label, field, day }) => {
          const dateValue = cycle[field];
          const isPast = actualCurrentDay > day;
          const isCurrent = actualCurrentDay === day;
          const isFuture = actualCurrentDay < day;

          return (
            <div 
              key={field}
              className={`flex items-center justify-between p-3 rounded-lg border ${
                isCurrent ? 'bg-green-50 border-green-300' :
                isPast ? 'bg-gray-50 border-gray-200' :
                'bg-white border-gray-200'
              }`}
            >
              <div>
                <p className={`text-sm font-medium ${isCurrent ? 'text-green-700' : ''}`}>
                  {label}
                </p>
                {isCurrent && <Badge className="bg-green-500 text-xs mt-1">TODAY</Badge>}
              </div>
              <span className={`font-medium ${
                isCurrent ? 'text-green-700' :
                isPast ? 'text-gray-500' : 'text-gray-700'
              }`}>
                {safeFormat(dateValue, 'MMM d, yyyy')}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px]" data-testid="room-detail-popup">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sprout className="w-5 h-5 text-[#1A4D2E]" />
              {room.name} - {isToday ? "Today's Data" : safeFormat(selectedDate, 'MMM d, yyyy')}
            </div>
          </DialogTitle>
        </DialogHeader>
        
        {/* Date Picker */}
        <div className="flex items-center justify-between bg-blue-50 p-3 rounded-lg mb-4">
          <span className="text-sm font-medium text-blue-800">View Data For:</span>
          <input
            type="date"
            value={selectedDate}
            onChange={handleDateChange}
            max={new Date().toISOString().split('T')[0]}
            className="px-3 py-1.5 border border-blue-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="date-picker"
          />
        </div>
        
        {/* Room Info - with calculated day */}
        <div className="bg-gray-50 p-3 rounded-lg mb-4">
          {cycle ? (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">{cycle.batch_name}</p>
                <p className="text-xs text-gray-500">Day {actualCurrentDay} / 45</p>
              </div>
              <Badge className="bg-green-100 text-green-800">
                {cycle.current_phase?.replace('_', ' ').toUpperCase()}
              </Badge>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No active cycle</p>
          )}
        </div>
        
        <Tabs defaultValue="dates" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="dates" data-testid="dates-tab">
              <Calendar className="w-4 h-4 mr-2" />
              Dates
            </TabsTrigger>
            <TabsTrigger value="temperature" data-testid="temp-tab">
              <Thermometer className="w-4 h-4 mr-2" />
              Temperature
            </TabsTrigger>
            <TabsTrigger value="watering" data-testid="watering-tab">
              <Droplets className="w-4 h-4 mr-2" />
              Watering
            </TabsTrigger>
            <TabsTrigger value="photos" data-testid="photos-tab">
              <ImageIcon className="w-4 h-4 mr-2" />
              Photos{photos.length > 0 ? ` (${photos.length})` : ''}
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="dates" className="mt-4">
            {renderDatesContent()}
          </TabsContent>
          
          <TabsContent value="temperature" className="mt-4">
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : (
              <Tabs defaultValue="morning" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="morning" className="flex items-center gap-1">
                    <Sun className="w-4 h-4" />
                    Morning
                    {temperatureData.morning && (
                      temperatureData.morning.is_complete === false
                        ? <AlertCircle className="w-3 h-3 text-amber-500 ml-1" />
                        : <CheckCircle className="w-3 h-3 text-green-500 ml-1" />
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="evening" className="flex items-center gap-1">
                    <Moon className="w-4 h-4" />
                    Evening
                    {temperatureData.evening && (
                      temperatureData.evening.is_complete === false
                        ? <AlertCircle className="w-3 h-3 text-amber-500 ml-1" />
                        : <CheckCircle className="w-3 h-3 text-green-500 ml-1" />
                    )}
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="morning">
                  {renderTemperatureContent(temperatureData.morning, 'morning')}
                </TabsContent>
                
                <TabsContent value="evening">
                  {renderTemperatureContent(temperatureData.evening, 'evening')}
                </TabsContent>
              </Tabs>
            )}
          </TabsContent>
          
          <TabsContent value="watering" className="mt-4">
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : wateringData.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Droplets className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No watering scheduled {isToday ? 'for today' : `on ${safeFormat(selectedDate, 'MMM d, yyyy')}`}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {wateringData.map((watering, idx) => (
                  <div 
                    key={idx}
                    className={`p-4 rounded-lg border ${
                      watering.completed 
                        ? 'bg-green-50 border-green-200' 
                        : 'bg-yellow-50 border-yellow-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">
                          {watering.water_amount_liters > 0 
                            ? `${watering.water_amount_liters} liters` 
                            : 'Watering Task'}
                        </p>
                        <p className="text-xs text-gray-500">
                          Scheduled: {safeFormat(watering.scheduled_date, 'MMM d, h:mm a')}
                        </p>
                      </div>
                      <Badge className={watering.completed ? 'bg-green-500' : 'bg-yellow-500'}>
                        {watering.completed ? (
                          <><CheckCircle className="w-3 h-3 mr-1" /> Done</>
                        ) : (
                          <><Clock className="w-3 h-3 mr-1" /> Pending</>
                        )}
                      </Badge>
                    </div>
                    {watering.completed && watering.completed_at && (
                      <p className="text-xs text-green-600 mt-2">
                        Completed at: {safeFormat(watering.completed_at, 'h:mm a')}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="photos" className="mt-4">
            {photos.length === 0 ? (
              <div className="text-center py-8 text-gray-500" data-testid="no-room-photos">
                <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No task photos for this room yet</p>
                <p className="text-xs mt-1">Photos appear here when supervisors complete tasks like Casing, Ruffling, Covering, Turning or Spawning.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="room-photos-list">
                {photos.map((p, idx) => (
                  <div key={`${p.photo_id}-${idx}`} className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 bg-white">
                    <TaskPhotoThumb photoId={p.photo_id} size="w-16 h-16" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900">{p.task}</span>
                        <Badge className={p.source === 'compost' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}>
                          {p.source === 'compost' ? 'Composting' : 'Growing'}
                        </Badge>
                        {p.day != null && <span className="text-xs text-gray-400">Day {p.day}</span>}
                      </div>
                      {p.completed_at && (
                        <p className="text-xs text-gray-500 mt-0.5">{safeFormat(p.completed_at, 'MMM d, yyyy h:mm a')}</p>
                      )}
                      {p.notes && <p className="text-xs text-gray-600 mt-1">{p.notes}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

const FarmOverview = () => {
  const [rooms, setRooms] = useState([]);
  const [roomCycles, setRoomCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoom, setSelectedRoom] = useState(null);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [roomsRes, cyclesRes] = await Promise.all([
        axios.get(`${API}/rooms`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/room-cycles`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setRooms(roomsRes.data);
      // Handle both array and object with 'cycles' key response formats
      const cyclesData = Array.isArray(cyclesRes.data) ? cyclesRes.data : (cyclesRes.data.cycles || []);
      setRoomCycles(cyclesData);
    } catch (error) {
      toast.error('Failed to fetch farm data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  // Normalize phase name to match expected format
  const normalizePhase = (phase) => {
    if (!phase) return 'idle';
    // Convert to lowercase and replace spaces with underscores
    const normalized = phase.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
    // Handle special cases
    if (normalized === 'casing_run') return 'case_run';
    if (normalized === 'pre_flush_2') return 'rest';
    return normalized;
  };

  const getPhaseColor = (phase) => {
    const normalizedPhase = normalizePhase(phase);
    const colors = {
      spawning: 'bg-purple-500',
      spawn_run: 'bg-purple-400',
      casing: 'bg-blue-500',
      case_run: 'bg-blue-400',
      ruffling: 'bg-cyan-500',
      recovery: 'bg-violet-500',
      covering: 'bg-teal-500',
      airing: 'bg-green-400',
      flush_1: 'bg-yellow-500',
      rest: 'bg-gray-400',
      flush_2: 'bg-orange-500',
      completed: 'bg-gray-300',
      scheduled: 'bg-blue-200',
      idle: 'bg-gray-200'
    };
    return colors[normalizedPhase] || 'bg-gray-200';
  };

  const getPhaseLabel = (phase) => {
    const normalizedPhase = normalizePhase(phase);
    const labels = {
      spawning: 'Spawning',
      spawn_run: 'Spawn Run',
      casing: 'Casing',
      case_run: 'Case Run',
      ruffling: 'Ruffling',
      recovery: 'Recovery',
      covering: 'Covering',
      airing: 'Airing',
      flush_1: 'Flush 1',
      rest: 'Rest',
      flush_2: 'Flush 2',
      completed: 'Completed',
      scheduled: 'Scheduled',
      idle: 'Idle'
    };
    return labels[normalizedPhase] || phase;
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading farm overview...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Legend */}
      <Card className="border-gray-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Room Status Legend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {['idle', 'spawning', 'spawn_run', 'casing', 'case_run', 'ruffling', 'covering', 'airing', 'flush_1', 'rest', 'flush_2'].map(phase => (
              <div key={phase} className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded ${getPhaseColor(phase)}`}></div>
                <span className="text-xs text-gray-600">{getPhaseLabel(phase)}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Farm Grid - 4x4 Layout */}
      <Card className="border-gray-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sprout className="w-5 h-5 text-[#1A4D2E]" />
            Farm Overview - All {rooms.length} Rooms
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="farm-overview-grid">
            {rooms.map((room) => {
              const activeCycle = roomCycles.find(c => c.room_id === room.id && c.status === 'active');
              const phase = activeCycle?.current_phase || 'idle';
              const normalizedPhase = normalizePhase(phase);
              const day = activeCycle?.current_day || 0;
              const batchName = activeCycle?.batch_name || '-';

              return (
                <div
                  key={room.id}
                  className={`relative p-4 rounded-xl border-2 ${
                    normalizedPhase === 'idle' ? 'border-gray-200 bg-gray-50' :
                    normalizedPhase.includes('flush') ? 'border-yellow-300 bg-yellow-50' :
                    'border-green-200 bg-green-50'
                  } transition-all hover:shadow-lg cursor-pointer`}
                  data-testid={`room-overview-${room.room_number}`}
                  onClick={() => setSelectedRoom({ room, cycle: activeCycle })}
                >
                  {/* Phase Indicator Bar */}
                  <div className={`absolute top-0 left-0 right-0 h-1.5 rounded-t-xl ${getPhaseColor(phase)}`}></div>
                  
                  {/* Room Number */}
                  <div className="flex items-center justify-between mb-3 mt-1">
                    <span className="text-lg font-bold text-gray-900">{room.name}</span>
                    <Badge 
                      className={`text-xs ${
                        normalizedPhase.includes('flush') ? 'bg-yellow-100 text-yellow-800' :
                        normalizedPhase === 'idle' ? 'bg-gray-100 text-gray-600' :
                        'bg-green-100 text-green-800'
                      }`}
                    >
                      {getPhaseLabel(phase)}
                    </Badge>
                  </div>

                  {/* Cycle Info */}
                  {activeCycle ? (
                    <div className="space-y-2">
                      <div className="text-xs text-gray-600">
                        <span className="font-medium">{batchName}</span>
                      </div>
                      <div className="text-xs text-gray-500">
                        Day {day} / 45
                      </div>
                      {/* Progress Bar */}
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full ${getPhaseColor(phase)}`}
                          style={{ width: `${(day / 45) * 100}%` }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500">No active cycle</div>
                  )}

                  {/* Environment Data — show N/A for empty (idle) rooms */}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 pt-3 border-t border-gray-200">
                    <div className="flex items-center gap-1 text-xs text-gray-600" title="Air temperature">
                      <Thermometer className="w-3 h-3" />
                      <span>{activeCycle && room.temperature != null ? `${room.temperature}°C` : 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-orange-700" title="Average compost temperature">
                      <Layers className="w-3 h-3" />
                      <span>{activeCycle && room.compost_avg_temp != null ? `${room.compost_avg_temp}°C` : 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-600" title="CO₂ level">
                      <Wind className="w-3 h-3" />
                      <span>{activeCycle && room.co2_level != null ? `${room.co2_level}ppm` : 'N/A'}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-gray-200">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-gray-900">
              {rooms.filter(r => roomCycles.find(c => c.room_id === r.id && c.status === 'active')).length}
            </div>
            <p className="text-sm text-gray-500">Active Rooms</p>
          </CardContent>
        </Card>
        <Card className="border-gray-200">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-yellow-600">
              {roomCycles.filter(c => c.status === 'active' && normalizePhase(c.current_phase).includes('flush')).length}
            </div>
            <p className="text-sm text-gray-500">Harvesting</p>
          </CardContent>
        </Card>
        <Card className="border-gray-200">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-green-600">
              {roomCycles.filter(c => c.status === 'active' && normalizePhase(c.current_phase) === 'airing').length}
            </div>
            <p className="text-sm text-gray-500">Airing</p>
          </CardContent>
        </Card>
        <Card className="border-gray-200">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-gray-600">
              {rooms.filter(r => !roomCycles.find(c => c.room_id === r.id && c.status === 'active')).length}
            </div>
            <p className="text-sm text-gray-500">Idle Rooms</p>
          </CardContent>
        </Card>
      </div>

      {/* Room Detail Popup */}
      {selectedRoom && (
        <RoomDetailPopup
          room={selectedRoom.room}
          cycle={selectedRoom.cycle}
          onClose={() => setSelectedRoom(null)}
        />
      )}
    </div>
  );
};

export default FarmOverview;
